import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["ref"],
    _excluded2 = ["autoComplete", "classes", "disabled", "displayEmpty", "invalid", "autoFocus", "mobilePopover", "readOnly", "filter", "iconLeft", "iconLeftProps", "iconRight", "iconRightProps", "label", "loadSuggestions", "mobileSearchPlaceholder", "onBlur", "onChange", "onClose", "onFocus", "onOpen", "onSuggestionSelected", "options", "placeholder", "size", "subText", "success", "theme", "tooltip", "tooltipAbsolute", "tooltipProps", "value", "visibleSuggestionsCount", "namespace", "mask", "maxLength", "name", "id", "subTextProps", "inputProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import Loader from "../Loader";
import { defaultPropObject } from "../constants";
import { useCombinedRefs, useMediaQuery, useWindowSize } from "../hooks";
import useDisableScroll from "../hooks/useDisableScroll";
import { ErrorCircleFilled } from "../icons";
import { ClickOutside } from "../internal/ClickOutside";
import { FieldIcon, FieldInput, FieldRoot, useField } from "../internal/Field";
import { isAppleMobile } from "../utils/detectDevice";
import { keyCode } from "../utils/keyCodes";
import noop from "../utils/noop";
import { Dropdown, Suggestions } from './components';
import { filterOptions, getCallId, getScrollbarProps } from './utils';
import styles from './SuggestField.css';
var cx = classNames.bind(styles);
export var SuggestField = /*#__PURE__*/forwardRef(function SuggestField(_ref, ref) {
  var _suggestions$highligh;

  var autoComplete = _ref.autoComplete,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$displayEmpty = _ref.displayEmpty,
      displayEmpty = _ref$displayEmpty === void 0 ? false : _ref$displayEmpty,
      _ref$invalid = _ref.invalid,
      invalid = _ref$invalid === void 0 ? false : _ref$invalid,
      _ref$autoFocus = _ref.autoFocus,
      autoFocus = _ref$autoFocus === void 0 ? false : _ref$autoFocus,
      _ref$mobilePopover = _ref.mobilePopover,
      mobilePopover = _ref$mobilePopover === void 0 ? false : _ref$mobilePopover,
      _ref$readOnly = _ref.readOnly,
      readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
      filter = _ref.filter,
      iconLeft = _ref.iconLeft,
      iconLeftProps = _ref.iconLeftProps,
      iconRight = _ref.iconRight,
      iconRightProps = _ref.iconRightProps,
      label = _ref.label,
      loadSuggestions = _ref.loadSuggestions,
      _ref$mobileSearchPlac = _ref.mobileSearchPlaceholder,
      mobileSearchPlaceholder = _ref$mobileSearchPlac === void 0 ? 'Поиск' : _ref$mobileSearchPlac,
      _ref$onBlur = _ref.onBlur,
      onBlur = _ref$onBlur === void 0 ? noop : _ref$onBlur,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? noop : _ref$onClose,
      _ref$onFocus = _ref.onFocus,
      onFocus = _ref$onFocus === void 0 ? noop : _ref$onFocus,
      _ref$onOpen = _ref.onOpen,
      onOpen = _ref$onOpen === void 0 ? noop : _ref$onOpen,
      _ref$onSuggestionSele = _ref.onSuggestionSelected,
      onSuggestionSelected = _ref$onSuggestionSele === void 0 ? noop : _ref$onSuggestionSele,
      options = _ref.options,
      placeholder = _ref.placeholder,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      subText = _ref.subText,
      success = _ref.success,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'b2c' : _ref$theme,
      tooltip = _ref.tooltip,
      tooltipAbsolute = _ref.tooltipAbsolute,
      tooltipProps = _ref.tooltipProps,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? '' : _ref$value,
      _ref$visibleSuggestio = _ref.visibleSuggestionsCount,
      visibleSuggestionsCount = _ref$visibleSuggestio === void 0 ? 3 : _ref$visibleSuggestio,
      namespace = _ref.namespace,
      mask = _ref.mask,
      maxLength = _ref.maxLength,
      name = _ref.name,
      id = _ref.id,
      subTextProps = _ref.subTextProps,
      _ref$inputProps = _ref.inputProps;
  _ref$inputProps = _ref$inputProps === void 0 ? defaultPropObject : _ref$inputProps;

  var inputPropsRef = _ref$inputProps.ref,
      inputProps = _objectWithoutProperties(_ref$inputProps, _excluded),
      rest = _objectWithoutProperties(_ref, _excluded2);

  var _useWindowSize = useWindowSize(),
      height = _useWindowSize.height;

  var breakpoints = useMediaQuery('xs');
  var isMobile = breakpoints.xs;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      mobileInputFocus = _useState4[0],
      setMobileInputFocus = _useState4[1];

  var _useState5 = useState(-1),
      _useState6 = _slicedToArray(_useState5, 2),
      highlighted = _useState6[0],
      setHighlighted = _useState6[1];

  var rootRef = useRef(null);
  var rootCombinedRef = useCombinedRefs(rootRef, ref);
  var lastCallId = useRef(null);
  var inputRef = useRef(null);
  var combinedRef = useCombinedRefs(inputRef, inputPropsRef);
  var inputMobileRef = useRef(null);
  var scrollbarRef = useRef(null);
  var clickOutsideContainerRef = useRef(null);

  var _useField = useField({
    onBlur: onBlur,
    onFocus: onFocus,
    namespace: namespace,
    componentName: 'suggestField',
    ref: inputRef
  }),
      focused = _useField.focused,
      handleBlurHook = _useField.handleBlur,
      handleFocusHook = _useField.handleFocus,
      getTestId = _useField.getTestId,
      isAutofill = _useField.isAutofill,
      getNamespace = _useField.getNamespace;

  var _useState7 = useState(function () {
    var val = value.trim();

    if (!val && options) {
      lastCallId.current = getCallId();
      return displayEmpty ? options : [];
    }

    return filterOptions({
      val: val,
      options: options,
      filter: filter
    });
  }),
      _useState8 = _slicedToArray(_useState7, 2),
      suggestions = _useState8[0],
      setSuggestions = _useState8[1];

  var valueEqualOption = useMemo(function () {
    return suggestions.some(function (item) {
      return (item === null || item === void 0 ? void 0 : item.label) === value;
    });
  }, [suggestions, value]);
  var showDropdown = isOpen && (isMobile || suggestions.length > 0);
  var handleChange = useCallback(function (e) {
    onChange(e.target.value);
    setSuggestions(filterOptions({
      val: e.target.value,
      options: options,
      filter: filter
    }));

    if (!e.target.value && !isMobile) {
      setIsOpen(false);
      onClose();
    }
  }, [filter, isMobile, onChange, onClose, options]);
  var handleSuggestionSelect = useCallback(function (suggest) {
    var _inputRef$current;

    onChange(suggest.label);
    onSuggestionSelected(suggest);
    setIsOpen(false);
    onClose(suggest); // После выбора опции необходимо вернуть фокус в input

    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  }, [onChange, onClose, onSuggestionSelected]);
  var handleHighlightedSet = useCallback(function (direction) {
    var set = highlighted + direction;

    if (set < 0) {
      setHighlighted(0);
      return;
    }

    if (set >= suggestions.length) {
      setHighlighted(suggestions.length - 1);
      return;
    }

    setHighlighted(set);
  }, [suggestions, highlighted]);
  var handleClosePopup = useCallback(function () {
    var _inputRef$current2;

    setIsOpen(false);
    onClose(); // После клика вне, необходимо вернуть фокус в input

    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.focus();
  }, [onClose]);
  var handleOpenPopup = useCallback(function () {
    setIsOpen(true);
    onOpen();
  }, [onOpen]);
  var handleMobileFocus = useCallback(function () {
    setMobileInputFocus(true);
  }, []);
  var handleMobileBlur = useCallback(function () {
    setTimeout(function () {
      setMobileInputFocus(false);
    }, 0);
  }, []);
  var handleHighlighted = useCallback(function () {
    if (suggestions.length) {
      handleSuggestionSelect(suggestions[highlighted < 0 ? 0 : highlighted]);
    } else {
      handleClosePopup();
    }
  }, [suggestions, handleSuggestionSelect, highlighted, handleClosePopup]);
  var handleKeyDown = useCallback(function (e) {
    if ([keyCode.ENTER, keyCode.MAC_ENTER].indexOf(e.keyCode) >= 0 && showDropdown) {
      handleHighlighted();
    }

    if (e.keyCode === keyCode.UP) {
      handleHighlightedSet(-1);
      e.preventDefault();
    }

    if (e.keyCode === keyCode.DOWN) {
      handleHighlightedSet(1);
      e.preventDefault();
    }
  }, // suggestions Решает проблему с рассинхронизацией опций по keyCode.ENTER
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [handleHighlighted, suggestions, handleHighlightedSet, showDropdown, highlighted]);
  var handleSetSuggestions = useCallback( // eslint-disable-next-line no-shadow
  function (id, inputValue, displayEmpty) {
    return function (val) {
      if (id === lastCallId.current && !!inputValue.trim() || displayEmpty) {
        setSuggestions(val);
        setHighlighted(-1);
      }
    };
  }, []);
  var handleAsyncSuggestions = useCallback(function (val) {
    var uniqId = getCallId();
    lastCallId.current = uniqId;

    if (loadSuggestions) {
      loadSuggestions(val, handleSetSuggestions(uniqId, value, displayEmpty));
    }
  }, [displayEmpty, handleSetSuggestions, loadSuggestions, value]);
  var handleBlur = useCallback(function (e) {
    if ([rootRef, clickOutsideContainerRef].some(function (item) {
      var _item$current;

      return item === null || item === void 0 ? void 0 : (_item$current = item.current) === null || _item$current === void 0 ? void 0 : _item$current.contains(e.relatedTarget);
    }) || showDropdown) {
      return;
    }

    handleBlurHook(e);
  }, [handleBlurHook, showDropdown]);
  var handleFocus = useCallback(function (e) {
    if (displayEmpty && !options) {
      var _val = value.trim();

      handleAsyncSuggestions(_val);
    }

    handleFocusHook(e);
  }, [displayEmpty, options, handleFocusHook, value, handleAsyncSuggestions]);

  var handleClickMobileButton = function handleClickMobileButton() {
    handleOpenPopup();

    if (!focused) {
      var _inputRef$current3;

      (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.focus();
    }
  };

  useEffect(function () {
    var val = value.trim();
    setHighlighted(-1);

    if (loadSuggestions) {
      handleAsyncSuggestions(val);
    }
  }, [options, displayEmpty, value, loadSuggestions, filter, handleAsyncSuggestions]);
  useEffect(function () {
    // Открываем окно при стандартном поведении
    if (!valueEqualOption && focused && suggestions.length > 0 && value) {
      handleOpenPopup();
    } // Срабатывает в тех случаях когда value пустое и указан displayEmpty


    if (suggestions.length > 0 && displayEmpty && !value && focused) {
      handleOpenPopup();
    }
  }, [focused, handleOpenPopup, suggestions.length, valueEqualOption, value, displayEmpty]);
  var scrollbarProps = useMemo(function () {
    return getScrollbarProps({
      height: height,
      isMobile: isMobile,
      mobilePopover: mobilePopover,
      visibleSuggestionsCount: visibleSuggestionsCount
    });
  }, [height, isMobile, mobilePopover, visibleSuggestionsCount]);
  var appleKeyboardHack = useMemo(function () {
    return mobileInputFocus && isAppleMobile();
  }, [mobileInputFocus]);
  useDisableScroll({
    isOpen: isOpen,
    target: document.body,
    onlyMobile: true
  });
  return /*#__PURE__*/React.createElement("div", _extends({}, rest, getTestId(), {
    className: cx('root', classes.root),
    ref: rootCombinedRef
  }), /*#__PURE__*/React.createElement(FieldRoot, {
    readOnly: readOnly,
    disabled: disabled,
    focused: focused,
    invalid: invalid,
    size: size,
    subText: subText,
    subTextProps: _objectSpread({
      errorIcon: /*#__PURE__*/React.createElement(ErrorCircleFilled, {
        size: "md"
      })
    }, subTextProps),
    success: success,
    tooltip: tooltip,
    tooltipAbsolute: tooltipAbsolute,
    tooltipProps: tooltipProps,
    autofill: isAutofill,
    theme: theme,
    namespace: getNamespace(),
    classes: {
      root: classes.fieldRoot,
      container: cx({
        popup_open: !isMobile && suggestions.length && isOpen
      }, classes.fieldContainer)
    }
  }, iconLeft && /*#__PURE__*/React.createElement(FieldIcon, _extends({
    position: "left",
    classes: {
      root: cx('icon', classes.fieldIcon)
    },
    loader: /*#__PURE__*/React.createElement(Loader, {
      size: "small"
    })
  }, iconLeftProps), iconLeft), /*#__PURE__*/React.createElement(FieldInput, _extends({}, inputProps, {
    classes: {
      root: classes.fieldInner,
      input: classes.fieldInput,
      label: classes.fieldLabel
    },
    onFocus: handleFocus,
    onChange: handleChange,
    onKeyPress: handleKeyDown,
    onBlur: handleBlur,
    readOnly: readOnly,
    disabled: disabled,
    placeholder: !label ? placeholder : undefined,
    type: "text",
    value: highlighted > -1 ? (_suggestions$highligh = suggestions[highlighted]) === null || _suggestions$highligh === void 0 ? void 0 : _suggestions$highligh.label : value,
    mask: mask,
    maxLength: maxLength,
    autoFocus: autoFocus,
    name: name,
    autoComplete: autoComplete,
    id: id,
    invalid: invalid,
    theme: theme,
    label: label,
    namespace: getNamespace(),
    ref: combinedRef
  })), iconRight && /*#__PURE__*/React.createElement(FieldIcon, _extends({
    invalid: invalid,
    theme: theme,
    position: "right",
    classes: {
      root: cx('icon', classes.fieldIcon)
    },
    loader: /*#__PURE__*/React.createElement(Loader, {
      size: "small"
    })
  }, iconRightProps), iconRight)), isMobile && !readOnly && !disabled && /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    onClick: handleClickMobileButton,
    className: cx('open', classes.open),
    "aria-hidden": "true"
  }, getTestId('button', 'mobileOpen'))), showDropdown && /*#__PURE__*/React.createElement(ClickOutside, {
    ref: clickOutsideContainerRef,
    onClickOutside: handleClosePopup
  }, /*#__PURE__*/React.createElement(Dropdown, {
    classes: classes,
    label: label || placeholder,
    mobile: isMobile,
    mobilePopover: mobilePopover,
    onBlur: handleMobileBlur,
    onChange: handleChange,
    onClose: handleClosePopup,
    onFocus: handleMobileFocus,
    onKeyDown: handleKeyDown,
    placeholder: mobileSearchPlaceholder,
    ref: inputMobileRef,
    size: size,
    value: value,
    namespace: getNamespace()
  }, /*#__PURE__*/React.createElement(Suggestions, {
    appleKeyboardHack: appleKeyboardHack,
    classes: classes,
    highlighted: highlighted,
    onSuggestionSelect: handleSuggestionSelect,
    ref: scrollbarRef,
    scrollbarProps: scrollbarProps,
    suggestions: suggestions,
    namespace: getNamespace()
  }))));
});
export default SuggestField;